import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import { PrivateRoute } from './components/privateRoute';
import Spinner from './components/spinner';
import { clubActions } from './actions/clubAction';
import { useDispatch } from 'react-redux';
import Loader from './components/loader';
import { RoleRoute } from './components/roleRoute';
import { Role, subDomain } from './actions/constants/common';
import AccessDenied from './pages/layout/accessDenied';
import JoinClub from './pages/club/joinClub';
import env from './services/env';
import { userConstants } from './actions/constants/userConstant';
import IdleTimer from './components/sessionTimer';
import { getCurrentTime, getIsTest, getIsValidSession, getRenewalTimeBeforeExpiry, hslToHex, setLabelColorForTheme } from './services/helper';
import { authActions } from './actions/authAction';
import Rsvp from './pages/event/rsvp';
import Sentiment from './pages/analytics/sentiment';
import { eventConstants } from './actions/constants/eventConstant';
import ErrorBoundary from './components/errorBoundary';
import { setNetworkErrorHandler } from './services/axios';
import NoInternetScreen from './components/noInternet';
import PageNotFound from './pages/layout/pageNotFound';
import { LoadingProvider } from './components/loadingContext';

const Layout = lazy(() => import('./pages/layout/layout'));
const Home = lazy(() => import('./pages/home/home'));
const Register = lazy(() => import('./pages/club/register'))
const Login = lazy(() => import('./pages/account/login'));
const ForgotPassword = lazy(() => import('./pages/account/forgotPassword'));
const ResetPassword = lazy(() => import('./pages/account/resetPassword'));
const ClubList = lazy(() => import('./pages/club/clubList'));
const ClubView = lazy(() => import('./pages/club/clubView'));
const ClubEdit = lazy(() => import('./pages/club/clubView'));
const ClubCreate = lazy(() => import('./pages/club/clubCreate'));
const ClubSetup = lazy(() => import('./pages/club/clubSetup'));
const PasswordSetup = lazy(() => import('./pages/club/passwordSetup'));
const RegistrationComplete = lazy(() => import('./pages/club/registrationComplete'));
const PublicClub = lazy(() => import('./pages/club/publicClub'));
const MyClub = lazy(() => import('./pages/club/myClub'));
const EventList = lazy(() => import('./pages/event/eventList'));
const CreateEvent = lazy(() => import('./pages/event/eventCreate'));
const UserList = lazy(() => import('./pages/user/userList'))
const Gallery = lazy(() => import('./pages/event/gallery'));
const PublicGallery = lazy(() => import('./pages/event/galleryView'));
const Profile = lazy(() => import('./pages/user/userProfile'));
const ChangePassword = lazy(() => import('./pages/user/changePassword'));
const CopyRight = lazy(() => import('./pages/layout/copyRight'));
const TermsAndConditions = lazy(() => import('./pages/layout/termsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/layout/privacyPolicy'));

var textSocket: WebSocket;
var imageSocket: WebSocket;

const App = () => {

    const dispatch = useDispatch();
    const host = location.host.split(".");

    const protocol = window.location.protocol;
    const socketProtocol = env.apiUrl.split(':')[0] === 'https' ? 'wss://' : 'ws://';
    var verifyToken: any = (new URLSearchParams(window.location.search)).get("token")
    const socketHost = new URL(env.apiUrl).host;

    //Redux state selectors
    const auth = useSelector((state: any) => state.authenticate);
    const renewToken = useSelector((state: any) => state.renewToken);
    const status = useSelector((state: any) => state.statusReducer);
    const domainData = useSelector((state: any) => state.getClubByDomain.domainResult);
    const textProcess = useSelector((state: any) => state.textProcess);
    const updateImageSentiment = useSelector((state: any) => state.updateImageSentiment);
    const clubDomain = useSelector((state: any) => state.getClubByDomain);

    //Local States
    const [domain, setDomain] = useState({
        clubDomain: (protocol === 'http:') ?
            (host.length > 1 ? host[0] : '') :
            (host.length > 2 ? host[0] : '')
    })
    const [session, setSession] = useState<boolean>(getIsValidSession(auth.authData))
    const [networkError, setNetworkError] = useState(false);
    const [user, setUser] = useState({
        email: "",
        password: '',
        clubDomain: domain.clubDomain,
        loginType: {
            isGoogle: false,
            isFacebook: false
        },
        isSocialLogin: false
    })

    let hue, saturation, lightness;

    if (verifyToken) {
        if (auth.isLoggedIn) {
            localStorage.removeItem('AUTHDATA');
            dispatch(authActions.userLogout());
        }
    }
    if (renewToken && renewToken.isSuccess && renewToken.isTokenInvalid) {
        localStorage.removeItem('AUTHDATA');
        window.location.href = '/login';
        renewToken.isTokenInvalid = false
    }
    if (domainData) {
        if (domainData.themeType)
            document.documentElement.setAttribute('color-scheme', domainData.themeType);
        let colorScheme = domainData.colorScheme;
        if (typeof colorScheme === 'number') {
            colorScheme = String(colorScheme);

        }
        const color = hslToHex(domainData.colorScheme)
        document.documentElement.style.setProperty('--_color', color);
        if (colorScheme) {
            if (colorScheme.includes(',')) {
                [hue, saturation, lightness] = colorScheme.split(',');
            } else {
                hue = colorScheme;
                saturation = '100%';
                lightness = '50%';
                colorScheme = `${hue},${saturation},${lightness}`;
            }
            setLabelColorForTheme(domainData?.themeType, colorScheme);
            document.documentElement.style.setProperty('--_hue', hue);
            document.documentElement.style.setProperty('--_saturation', saturation ?? '100');
            document.documentElement.style.setProperty('--_lightness', lightness ?? '50');
        }
    }

    useEffect(() => {
        if (domainData) {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setLabelColorForTheme(prefersDark ? 'light' : 'dark', domainData?.colorScheme);
        }
    }, []);

    useEffect(() => {
        const tokenExpiry = auth?.authData?.tokenExpery;
        const currentTime = getCurrentTime()

        if (domain.clubDomain === subDomain.www) {
            const updatedUrl = window.location.href.replace("//www.", "//");
            window.location.replace(updatedUrl);
        } else if (domain.clubDomain !== "") {
            dispatch(clubActions.getClubDomain(domain));
        }
        if (currentTime >= tokenExpiry) {
            localStorage.removeItem('AUTHDATA');
            window.location.href = '/login';
        }
        if (auth?.authData) {
            if (session) {
                const currentTime = getCurrentTime()
                const renewalTime = getRenewalTimeBeforeExpiry(auth?.authData?.tokenExpery)
                if (auth?.authData?.token && currentTime > renewalTime)
                    dispatch(authActions.renewToken({ token: auth?.authData?.token, isMobile: false }))
            } else {
                localStorage.removeItem('AUTHDATA');
                window.location.href = '/login';
            }
        }
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [])

    useEffect(() => {
        if (status && status.isShowSuccess && !status.isShowError) {
            toast.success(status.message);
            status.isShowSuccess = false;
            status.message = ""
        }
        if (status && !status.isShowSuccess && status.isShowError) {
            toast.error(status.message);
            status.isShowError = false;
            status.message = ""
        }
    }, [status])
    useEffect(() => {
        setSession(getIsValidSession(auth.authData))
    }, [auth])

    useEffect(() => {
        if (!networkError && auth && auth.isLoggedIn && !getIsTest()) {
            const webSocket = new WebSocket(`${socketProtocol}${socketHost}/notification`, auth?.authData?.token);
            webSocket.onopen = (e) => { };
            webSocket.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data >= 0) {
                    if (typeof data != 'object') {
                        dispatch({ type: userConstants.getNotificationCountSuccess, payload: data })
                    }
                }
            };
        }
    }, [auth.isLoggedIn, dispatch]);

    useEffect(() => {
        setNetworkErrorHandler(setNetworkError);
    }, [])

    useEffect(() => {
        if (textProcess.isLoading) {
            if (auth.isLoggedIn && !getIsTest()) {
                textSocket = new WebSocket(`${socketProtocol}${socketHost}/textProgress`, [auth?.authData?.token, textProcess.eventId]);
                textSocket.onopen = (e) => { };
                textSocket.onmessage = (event) => {
                    const data = JSON.parse(event.data);
                    if (!data.progress) dispatch({ type: eventConstants.textProcessStatusSuccess })
                };
            }
        }

        if (textProcess.isSuccess) {
            textSocket.close()
        }

        if (updateImageSentiment.isSuccess) {
            if (auth.isLoggedIn && !getIsTest() && updateImageSentiment?.data?.isAnalyzing) {
                imageSocket = new WebSocket(`${socketProtocol}${socketHost}/imageProgress`, [updateImageSentiment?.data?.clubId]);
                imageSocket.onopen = (e) => { };
                imageSocket.onmessage = (event) => {
                    const data = JSON.parse(event.data);
                    if (!data.progress)
                        dispatch({ type: eventConstants.imageProcessStatusSuccess })
                    dispatch({ type: eventConstants.imageProcessStatusSuccess })
                };
            }
        }
    }, [textProcess, updateImageSentiment])

    return (
        <div className="App">
            <LoadingProvider>
                <ToastContainer theme="colored" />
                <div id="zmmtg-root"></div>
                {auth.isLoggedIn && <IdleTimer />}
                {networkError ? (
                    <NoInternetScreen />
                ) : (
                    <BrowserRouter>
                        <ErrorBoundary>
                            {clubDomain.isError ? (<PageNotFound />) : (
                                <Layout>
                                    <Routes>
                                        <Route element={<PrivateRoute />}>
                                            <Route path='club/*' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager]} />}>
                                                <Route element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><ClubList /></Suspense>} />
                                                </Route>
                                                <Route path='create' element={<RoleRoute roles={[Role.admin]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><ClubCreate /></Suspense>} />
                                                </Route>
                                                <Route path='view' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><ClubView /></Suspense>} />
                                                </Route>
                                                <Route path='edit' element={<RoleRoute roles={[Role.admin, Role.clubOwner]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><ClubEdit /></Suspense>} />
                                                </Route>
                                                <Route path='myclub' element={<RoleRoute roles={[Role.clubOwner]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><MyClub /></Suspense>} />
                                                </Route>
                                                <Route path='analytics' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><Sentiment /></Suspense>} />
                                                </Route>
                                            </Route>
                                            <Route path='event/*' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager, Role.Member]} />}>
                                                <Route index element={<Suspense fallback={<Spinner />}><EventList /></Suspense>} />
                                                <Route path="create" element={<RoleRoute roles={[Role.admin, Role.clubOwner]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><CreateEvent /></Suspense>} />
                                                </Route>
                                                <Route path='gallery' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager, Role.Member]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><Gallery /></Suspense>} />
                                                </Route>
                                                <Route path='analytics' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager, Role.Member]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><Sentiment /></Suspense>} />
                                                </Route>
                                            </Route>
                                            <Route path='analytics/*' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager, Role.Member]} />}>
                                                <Route index element={<Suspense fallback={<Spinner />}><Sentiment /></Suspense>} />
                                            </Route>
                                            <Route path='user/*' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager, Role.Member]} />}>
                                                <Route element={<RoleRoute roles={[Role.admin, Role.clubOwner]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><UserList /> </Suspense>} />
                                                </Route>
                                                <Route path='profile/view' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager, Role.Member]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><Profile /></Suspense>} />
                                                </Route>
                                                <Route path='passwordchange' element={<RoleRoute roles={[Role.admin, Role.clubOwner, Role.clubManager, Role.Member]} />}>
                                                    <Route index element={<Suspense fallback={<Spinner />}><ChangePassword /></Suspense>} />
                                                </Route>
                                                <Route path='gallery' element={<Suspense fallback={<Spinner />}><PublicGallery /></Suspense>} />
                                            </Route>
                                        </Route>
                                        <Route><Route path="galleries/*" element={<Suspense fallback={<Loader />} ><PublicGallery /></Suspense>} /></Route>
                                        <Route path="/" element={<Suspense fallback={<Loader />} ><Home /></Suspense>}></Route>

                                        {(protocol === 'http:' ? host.length > 1 : host.length > 2) ? (<>
                                            <Route><Route path="/login" element={<Suspense fallback={<Loader />}><Login /></Suspense>} /></Route>

                                        </>
                                        ) : (
                                            <>
                                                {auth && !auth.isLoggedIn && !session && (
                                                    <Route>
                                                        <Route><Route path='/clubs' element={<Suspense fallback={<Loader />}> <PublicClub /></Suspense>} /></Route>
                                                        <Route><Route path='/register-1' element={<Suspense fallback={<Loader />}> <Register /></Suspense>} /></Route>
                                                        <Route><Route path='/register-2' element={<Suspense fallback={<Loader />}> <Register /></Suspense>} /></Route>
                                                        <Route><Route path='/clubsetup' element={<Suspense fallback={<Loader />}> <ClubSetup /></Suspense>} /></Route>
                                                    </Route>
                                                )}
                                            </>
                                        )}
                                        {!session &&
                                            <Route>
                                                <Route><Route path="/members/rsvp" element={<Suspense fallback={<Loader />}><Rsvp /></Suspense>} /></Route>
                                                <Route><Route path='/changepassword' element={<Suspense fallback={<Loader />}><ResetPassword /></Suspense>} /></Route>
                                                <Route><Route path='/forgotpassword' element={<Suspense fallback={<Loader />}><ForgotPassword /></Suspense>} /></Route>
                                                <Route><Route path='/privacypolicy' element={<Suspense fallback={<Loader />}><PrivacyPolicy /></Suspense>} /></Route>
                                                <Route><Route path='/updatepassword' element={<Suspense fallback={<Loader />}> <PasswordSetup /></Suspense>} /></Route>
                                                <Route><Route path='/passwordUpdated' element={<Suspense fallback={<Loader />}> <RegistrationComplete /></Suspense>} /></Route>
                                                <Route><Route path="galleries/*" element={<Suspense fallback={<Loader />}><PublicGallery /></Suspense>} /></Route>
                                                <Route><Route path='/join' element={<Suspense fallback={<Spinner />}><JoinClub /></Suspense>} /></Route>
                                                <Route><Route path='/registrationComplete' element={<Suspense fallback={<Loader />}> <RegistrationComplete /></Suspense>} /></Route>
                                                <Route><Route path='/invitationAccepted' element={<Suspense fallback={<Loader />}> <RegistrationComplete /></Suspense>} /></Route>
                                            </Route>
                                        }
                                        <Route path='/copyright' element={<Suspense fallback={<Loader />}><CopyRight /></Suspense>} />
                                        <Route path='/terms' element={<Suspense fallback={<Loader />}><TermsAndConditions /></Suspense>} />
                                        <Route path='/privacypolicy' element={<Suspense fallback={<Loader />}><PrivacyPolicy /></Suspense>} />
                                        <Route path='/403' element={<Suspense fallback={<Loader />}><AccessDenied /></Suspense>} />
                                        <Route path="*" element={<Navigate to="/" />} />
                                    </Routes>
                                </Layout>
                            )}
                        </ErrorBoundary>
                    </BrowserRouter>
                )}
            </LoadingProvider>
        </div>
    );
}

export default App;
